import { useEffect, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate, useParams } from "react-router-dom";

import { FullPageLoading } from "@/components/full-page-loading";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { refreshLinkAuthSession } from "@/hooks/use-link-auth-bootstrap";
import { env } from "@/lib/env";

export function SkedAuthPage() {
  useDocumentTitle("Signing in");
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    async function run() {
      if (!token) {
        setError("Missing authentication token.");
        return;
      }
      try {
        const response = await fetch(
          `${env.skedLinkBackendUrl}/auth/legacy-session`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
          }
        );
        if (!response.ok) {
          throw new Error(`Session exchange failed (${response.status})`);
        }
        if (!(await refreshLinkAuthSession())) {
          throw new Error("The new authentication session could not be loaded");
        }

        // Store Intercom identifiers so the widget can verify the user.
        try {
          const decoded = jwtDecode<{
            userId?: string;
            intercom?: { web?: string };
          }>(token);
          if (decoded.intercom?.web) {
            window.localStorage.setItem("intercom-hash", decoded.intercom.web);
          }
          if (decoded.userId) {
            window.localStorage.setItem("intercom-id", decoded.userId);
          }
        } catch {
          // Non-critical — Intercom will still work without verification.
        }

        navigate("/", { replace: true });
      } catch (error) {
        console.error("SkedAuth failed", error);
        setError("Sign-in failed. Please try again from Sked Social.");
      }
    }

    void run();
  }, [token, navigate]);

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-8 py-16 text-center">
        <h1 className="font-gotham text-2xl font-semibold text-neutral-900">
          {error}
        </h1>
      </div>
    );
  }

  return <FullPageLoading label="Signing you in with Sked Social…" />;
}
